import CustomTable from '../../../../components/CustomTable';
import CustomInput from '../../../../components/CustomInput';
import CustomModal from '../../../../components/CustomModal';

import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    getClientProducts,
    clientProductsLoadingStatus,
    clientProducts,
    oneClient,
    statusProductForClient,
    statusAccountTypeForProduct,
} from '../../../../redux/slices/clientsSlice';
import { toast } from 'react-toastify';
import { Checkbox, Space, Switch } from 'antd';
import { Helmet } from 'react-helmet';

export default function ClientProductsSubPage() {
    const ACTIVATION_COOLDOWN_STORAGE_KEY = 'productsActivationCooldowns';
    const ACTIVATION_COOLDOWN_MS = 10 * 60 * 1000;

    const dispatch = useDispatch();
    const products = useSelector(clientProducts);
    const clientsProductsStatus = useSelector(clientProductsLoadingStatus);
    const client = useSelector(oneClient);
    const { client_uid, api_url } = client;
    const [isDeactivateModalVisible, setIsDeactivateModalVisible] =
        useState(false);
    const [deactivationConfirmation, setDeactivationConfirmation] =
        useState('');
    const [pendingDeactivation, setPendingDeactivation] = useState(null);
    const [activationCooldowns, setActivationCooldowns] = useState({});
    const [currentTimestamp, setCurrentTimestamp] = useState(Date.now());
    const DEACTIVATION_WORD = 'deactivate';

    const getSanitizedCooldowns = (cooldowns, timestamp = Date.now()) => {
        if (!cooldowns || typeof cooldowns !== 'object') return {};

        return Object.entries(cooldowns).reduce((acc, [productId, endsAt]) => {
            const endsAtNumber = Number(endsAt);
            if (endsAtNumber > timestamp) {
                acc[productId] = endsAtNumber;
            }
            return acc;
        }, {});
    };

    const setActivationCooldownForProduct = (productId) => {
        if (!productId) return;

        setActivationCooldowns((prev) => {
            const next = {
                ...prev,
                [productId]: Date.now() + ACTIVATION_COOLDOWN_MS,
            };
            localStorage.setItem(
                ACTIVATION_COOLDOWN_STORAGE_KEY,
                JSON.stringify(next),
            );
            return next;
        });
    };

    const getRemainingActivationCooldown = (productId) => {
        if (!productId) return 0;
        const endsAt = activationCooldowns[productId];
        if (!endsAt) return 0;
        return Math.max(0, endsAt - currentTimestamp);
    };

    const formatRemainingTime = (milliseconds) => {
        const totalSeconds = Math.ceil(milliseconds / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;

        if (!minutes) {
            return `${seconds}s`;
        }

        if (!seconds) {
            return `${minutes}m`;
        }

        return `${minutes}m ${seconds}s`;
    };

    const blockActivationIfCoolingDown = (productId) => {
        const remainingCooldown = getRemainingActivationCooldown(productId);
        if (!remainingCooldown) return false;

        toast.warning(
            `This product can be activated again in ${formatRemainingTime(remainingCooldown)}.`,
        );
        return true;
    };

    useEffect(() => {
        const storedCooldowns = localStorage.getItem(
            ACTIVATION_COOLDOWN_STORAGE_KEY,
        );
        let parsedCooldowns = {};

        if (storedCooldowns) {
            try {
                parsedCooldowns = JSON.parse(storedCooldowns);
            } catch (error) {
                parsedCooldowns = {};
            }
        }

        const sanitizedCooldowns = getSanitizedCooldowns(parsedCooldowns);
        setActivationCooldowns(sanitizedCooldowns);
        localStorage.setItem(
            ACTIVATION_COOLDOWN_STORAGE_KEY,
            JSON.stringify(sanitizedCooldowns),
        );
    }, []);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTimestamp(Date.now());
        }, 1000);

        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        setActivationCooldowns((prev) => {
            const sanitizedCooldowns = getSanitizedCooldowns(
                prev,
                currentTimestamp,
            );
            if (
                Object.keys(sanitizedCooldowns).length ===
                Object.keys(prev).length
            ) {
                return prev;
            }

            localStorage.setItem(
                ACTIVATION_COOLDOWN_STORAGE_KEY,
                JSON.stringify(sanitizedCooldowns),
            );
            return sanitizedCooldowns;
        });
    }, [currentTimestamp]);

    const modifiedProductsForTable = products?.map((el) => {
        return {
            id: el.id,
            name: el?.title.replaceAll('_', '   ').toLowerCase(),
            active: el?.status === 'Enabled' ? true : false,
            BUSINESS:
                el.available_account_types[0].status === 'Enabled'
                    ? true
                    : false,
            SINGLE:
                el.available_account_types[1].status === 'Enabled'
                    ? true
                    : false,
            FAMILY:
                el.available_account_types[2].status === 'Enabled'
                    ? true
                    : false,
        };
    });

    const closeDeactivateModal = () => {
        setIsDeactivateModalVisible(false);
        setDeactivationConfirmation('');
        setPendingDeactivation(null);
    };

    const onDeactivationConfirmationChange = (e) => {
        setDeactivationConfirmation(e.target.value);
    };

    const applyTypeChange = async (checked, flag, i) => {
        const selectedAccountType = products[i]?.available_account_types.find(
            (type) => type.title === flag,
        );
        const enabledAccountType = products[i]?.available_account_types.some(
            (type) => type.status === 'Enabled',
        );
        const lastEnabledAccountType = [...products][i].available_account_types
            .filter((type) => type.status === 'Enabled')
            .map((type) => type.title);
        const lastDisabledAccountType = [...products][i].available_account_types
            .filter((type) => type.status === 'Disabled')
            .map((type) => type.title);
        const dataForProductStatus = {
            product_title: products[i]?.title,
        };
        const dataForAccountTypes = {
            product_title: products[i].title,
            account_type: selectedAccountType?.title,
        };

        if (checked && selectedAccountType) {
            if (enabledAccountType) {
                await dispatch(
                    statusAccountTypeForProduct({
                        action: 'enable',
                        client: client_uid,
                        body: dataForAccountTypes,
                        url: api_url,
                    }),
                );
            } else {
                await dispatch(
                    statusProductForClient({
                        action: 'enable',
                        client: client_uid,
                        body: dataForProductStatus,
                        url: api_url,
                    }),
                );
                await dispatch(
                    statusAccountTypeForProduct({
                        action: 'enable',
                        client: client_uid,
                        body: dataForAccountTypes,
                        url: api_url,
                    }),
                );
            }
            await dispatch(
                getClientProducts({ client: client_uid, url: api_url }),
            );
            toast.success('Changes saved successfully');
        }

        if (!checked && selectedAccountType) {
            await dispatch(
                statusAccountTypeForProduct({
                    action: 'disable',
                    client: client_uid,
                    body: dataForAccountTypes,
                    url: api_url,
                }),
            );
            if (lastEnabledAccountType.length === 1) {
                await dispatch(
                    statusProductForClient({
                        action: 'disable',
                        client: client_uid,
                        body: dataForProductStatus,
                        url: api_url,
                    }),
                );
            }
            await dispatch(
                getClientProducts({ client: client_uid, url: api_url }),
            );
            setActivationCooldownForProduct(products[i]?.id);
            toast.success('Changes saved successfully');
        }

        if (checked && !selectedAccountType) {
            await dispatch(
                statusProductForClient({
                    action: 'enable',
                    client: client_uid,
                    body: dataForProductStatus,
                    url: api_url,
                }),
            );
            for (const title of lastDisabledAccountType) {
                await dispatch(
                    statusAccountTypeForProduct({
                        action: 'enable',
                        client: client_uid,
                        body: { ...dataForAccountTypes, account_type: title },
                        url: api_url,
                    }),
                );
            }
            await dispatch(
                getClientProducts({ client: client_uid, url: api_url }),
            );
            toast.success('Changes saved successfully');
        }

        if (!checked && !selectedAccountType) {
            for (const title of lastEnabledAccountType) {
                await dispatch(
                    statusAccountTypeForProduct({
                        action: 'disable',
                        client: client_uid,
                        body: { ...dataForAccountTypes, account_type: title },
                        url: api_url,
                    }),
                );
            }
            await dispatch(
                statusProductForClient({
                    action: 'disable',
                    client: client_uid,
                    body: dataForProductStatus,
                    url: api_url,
                }),
            );
            await dispatch(
                getClientProducts({ client: client_uid, url: api_url }),
            );
            setActivationCooldownForProduct(products[i]?.id);
            toast.success('Changes saved successfully');
        }
    };

    const onDeactivateConfirm = async () => {
        if (
            deactivationConfirmation.toLowerCase() !== DEACTIVATION_WORD ||
            !pendingDeactivation
        ) {
            return;
        }

        await applyTypeChange(
            pendingDeactivation.checked,
            pendingDeactivation.flag,
            pendingDeactivation.i,
        );
        closeDeactivateModal();
    };

    const onTypeChange = async (checked, flag, i) => {
        const productId = products[i]?.id;

        if (checked) {
            if (blockActivationIfCoolingDown(productId)) {
                return;
            }
            await applyTypeChange(checked, flag, i);
            return;
        }

        setPendingDeactivation({
            checked,
            flag,
            i,
            productName: products[i]?.title?.replaceAll('_', ' ') || '',
        });
        setDeactivationConfirmation('');
        setIsDeactivateModalVisible(true);
    };

    const accountTypes = {
        title: 'Account types',
        render: (_, record, i) => (
            <Space size="middle">
                <div className="details-table__switch-wrapper">
                    <Checkbox
                        className="details-table__switch--main"
                        checked={record.BUSINESS}
                        onChange={(e) =>
                            onTypeChange(e.target.checked, 'BUSINESS', i)
                        }
                    >
                        Business
                    </Checkbox>
                </div>
                <div className="details-table__switch-wrapper">
                    <Checkbox
                        className="details-table__switch--main"
                        checked={record.SINGLE}
                        onChange={(e) =>
                            onTypeChange(e.target.checked, 'SINGLE', i)
                        }
                    >
                        Single
                    </Checkbox>
                </div>
                <div className="details-table__switch-wrapper">
                    <Checkbox
                        className="details-table__switch--main"
                        checked={record.FAMILY}
                        onChange={(e) =>
                            onTypeChange(e.target.checked, 'FAMILY', i)
                        }
                    >
                        Family
                    </Checkbox>
                </div>
            </Space>
        ),
    };

    const activeTypes = {
        title: 'Active',
        render: (_, record, i) => (
            <Space size="middle">
                <Switch
                    className="details-table__switch--general"
                    checked={record.active}
                    onChange={(e) => onTypeChange(e, 'active', i)}
                />
            </Space>
        ),
    };

    return (
        <>
            <Helmet>
                <title>{`Console Defentry - Clients - ${client.name} - Activated Products`}</title>
            </Helmet>
            <div className="details-table-wrapper">
                <CustomTable
                    tablePannelTitle="Products details"
                    tableHeader={['Product name', 'Account types', 'Active']}
                    functionalCell={{
                        'Account types': accountTypes,
                        Active: activeTypes,
                    }}
                    columnsDataMap={{
                        'Product name': 'name',
                    }}
                    isLoading={clientsProductsStatus === 'loading'}
                    data={modifiedProductsForTable}
                />
            </div>
            <CustomModal
                title="Deactivate product"
                subText={`Deactivating product "${pendingDeactivation?.productName || ''}" will cancel all active subscriptions for all accounts on related services and vendors.`}
                subTextAlign="left"
                isModalVisible={isDeactivateModalVisible}
                onAcceptButtonClick={onDeactivateConfirm}
                onCancelButtonClick={closeDeactivateModal}
                cancelButtonText="Cancel"
                acceptButtonText="Deactivate"
                isAcceptButtonDisabled={
                    deactivationConfirmation.toLowerCase() !== DEACTIVATION_WORD
                }
            >
                <p className="modal__sub-text" style={{ textAlign: 'left' }}>
                    Please type "deactivate" to continue.
                </p>
                <p
                    className="modal__sub-text"
                    style={{ textAlign: 'left', marginBottom: '16px' }}
                >
                    You won&apos;t be able to activate the product back during
                    next 10 minutes.
                </p>
                <CustomInput
                    id="deactivationConfirmation"
                    placeholder='Type "deactivate"'
                    onInputChange={onDeactivationConfirmationChange}
                    inputValue={deactivationConfirmation}
                    width="100%"
                />
            </CustomModal>
        </>
    );
}
